/**
 * Labstep
 *
 * @module components/EntityStateWorkflow/Modal/LeftPanel
 * @desc Left panel of the entity state workflow modal
 */

import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import EntityActionCreate from 'labstep-web/components/Entity/Action/Create';
import { EntityActionDelete } from 'labstep-web/components/Entity/Action/Delete';
import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { EntityUpdateAnyContainer } from 'labstep-web/containers/Entity/Update/Any';
import { Flex } from 'labstep-web/core/Flex';
import { Header } from 'labstep-web/core/Header';
import { Icon } from 'labstep-web/core/Icon';
import { List } from 'labstep-web/core/List';
import { StatusCard } from 'labstep-web/core/StatusCard';
import { TextWithHelp } from 'labstep-web/core/Text/WithHelp';
import {
  EntityState,
  EntityStateType,
} from 'labstep-web/models/entity-state.model';
import { EntityStateWorkflow } from 'labstep-web/models/entity-state-workflow.model';
import { PositionService } from 'labstep-web/services/position.service';
import type React from 'react';
import styles from './styles.module.scss';
import type { EntityStateWorkflowModalLeftPanelProps } from './types';
import {
  getHelperTextByType,
  getMaxPositionByType,
  getRandomColor,
} from './utils';

export const EntityStateWorkflowModalLeftPanel: React.FC<
  EntityStateWorkflowModalLeftPanelProps
> = ({ entityStateWorkflow, onSelect, selectedState }) => {
  const canEdit = useHasAccess(
    EntityStateWorkflow.entityName,
    entityStateWorkflow!.id,
    Action.edit,
  );
  return (
    <Flex
      data-testid="entity-state-workflow-modal-left-panel"
      className={styles.left}
      spacing="gap"
      column
    >
      {entityStateWorkflow
        ? Object.values(EntityStateType).map((type) => {
            const entityStates =
              entityStateWorkflow.getStatesWithType(type);

            return (
              <Flex
                key={type}
                column
                data-testid={`${type.toLocaleLowerCase()}`}
              >
                <Flex spacing="between" data-testid="header">
                  <Header size="mini">
                    <TextWithHelp
                      text={type}
                      helperText={getHelperTextByType(type)}
                    />
                  </Header>
                  <EntityActionCreate
                    entityName={EntityState.entityName}
                    parentName={entityStateWorkflow.entityName}
                    parentId={entityStateWorkflow.id}
                    body={{
                      name: 'Untitled State',
                      type: type.toLowerCase(),
                      color: getRandomColor(),
                      position: getMaxPositionByType(
                        entityStateWorkflow,
                        type,
                      ),
                    }}
                    options={{
                      onSuccess: ({ response }) => {
                        if (!response) {
                          return;
                        }
                        onSelect(response.result as any);
                      },
                    }}
                    actionComponentProps={{
                      icon: 'plus',
                      type: 'icon',
                    }}
                  />
                </Flex>
                <Flex
                  data-testid={`entity-states-${type.toLocaleLowerCase()}`}
                  spacing="gap"
                  column
                >
                  <EntityUpdateAnyContainer
                    entityName={EntityState.entityName}
                  >
                    {({ update }) => (
                      <DragDropContext
                        onDragEnd={(result: DropResult) => {
                          PositionService.onDragEnd(
                            result,
                            entityStates,
                            update as any,
                          );
                        }}
                      >
                        <Droppable
                          droppableId={`entity-state-list-${type.toLocaleLowerCase()}`}
                          isDropDisabled={!canEdit}
                        >
                          {(provided) => (
                            <div
                              id={`entity-state-list-${type.toLocaleLowerCase()}`}
                              ref={provided.innerRef}
                            >
                              <List>
                                {entityStates
                                  .sort(
                                    (a, b) => a.position - b.position,
                                  )
                                  .map((entityState, index) => (
                                    <Draggable
                                      key={`entityState-${entityState.id}`}
                                      draggableId={`entityState:${entityState.id}`}
                                      index={index}
                                    >
                                      {(draggableProvided) => (
                                        <div
                                          ref={
                                            draggableProvided.innerRef
                                          }
                                          {...draggableProvided.draggableProps}
                                        >
                                          <StatusCard
                                            key={entityState.id}
                                            text={entityState.name}
                                            color={entityState.color}
                                            icon={entityState.icon}
                                            onSelect={() => {
                                              onSelect(
                                                entityState.id,
                                              );
                                            }}
                                            onHoverComponent={
                                              <EntityActionDelete
                                                entity={entityState}
                                                actionComponentProps={{
                                                  type: 'icon',
                                                }}
                                              />
                                            }
                                            active={
                                              selectedState?.id ===
                                              entityState.id
                                            }
                                            dragHandle={
                                              <Icon
                                                {...draggableProvided.dragHandleProps}
                                                name="sort"
                                                popup={{
                                                  offset: [20, 0],
                                                  content:
                                                    'Drag to reorder',
                                                }}
                                              />
                                            }
                                          />
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                {provided.placeholder}
                              </List>
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}
                  </EntityUpdateAnyContainer>
                </Flex>
              </Flex>
            );
          })
        : null}
    </Flex>
  );
};
